import { useQuery } from '@tanstack/react-query';
import { isEmpty, isNil, toString } from 'lodash';
import {
  FUND_SUMMARY_BY_MD_CAP_IQ_API_ERROR,
  FUND_SUMMARY_BY_MD_DEFAULT_ERROR_MESSAGE,
  FUND_SUMMARY_BY_MD_QUERY_KEY,
  SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/funds';
import { SuperAgentError } from 'common/types/services';
import { FundService } from 'services';
import { throwError } from 'utilities';
import {
  GetFundSummaryParams,
  GetFundSummaryResponse,
  GetFundSummaryReturn,
  UseGetFundSummaryParams,
} from './useGetFundSummary.types';

const getFundSummary = async (params: GetFundSummaryParams): Promise<GetFundSummaryResponse | null> => {
  const { fundId, measurementDateSlug, measurementDate } = params;

  if (isNil(fundId)) {
    return null;
  }

  const fundService = new FundService();

  try {
    // Pass null when slug is empty/undefined (for "Most Recent") to omit date parameter
    // Otherwise, use the date property (YYYY-MM-DD format) instead of slug
    const dateParam = isEmpty(measurementDateSlug) ? null : (measurementDate?.date ?? null);
    const fundSummary = (await fundService.getFundSummary(
      toString(fundId),
      dateParam
    )) as GetFundSummaryResponse;

    if (!fundSummary) {
      return throwError(FUND_SUMMARY_BY_MD_DEFAULT_ERROR_MESSAGE);
    }

    return fundSummary;
  } catch (error) {
    const { response } = error as SuperAgentError;

    if (response?.body?.cap_iq_api) {
      return throwError(FUND_SUMMARY_BY_MD_CAP_IQ_API_ERROR, error);
    }

    return throwError(FUND_SUMMARY_BY_MD_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFundSummary = (params: UseGetFundSummaryParams): GetFundSummaryReturn => {
  const { fund, measurementDate, shouldQueryAutomatically = true } = params;

  const fundId = fund?.id;
  // Use slug to check if "Most Recent" (empty slug), but pass date property to endpoint
  const measurementDateSlug = measurementDate?.slug;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<GetFundSummaryReturn['data'], Error>({
    // Enable query when fundId exists and measurementDate is provided (slug can be empty for "Most Recent")
    enabled: !isNil(fundId) && measurementDate !== undefined && shouldQueryAutomatically,
    queryFn: () => getFundSummary({ fundId, measurementDateSlug, measurementDate }),
    queryKey: [FUND_SUMMARY_BY_MD_QUERY_KEY, fundId, measurementDateSlug ?? 'most-recent', measurementDate?.date],
    refetchOnWindowFocus: SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export { useGetFundSummary, getFundSummary };
